import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { detectAllergens, declarationName } from "@/lib/allergens";

/**
 * Scans an ingredient statement for allergen trigger words and warns when a
 * high-risk allergen is present but NOT declared (AU PEAL compliance). The
 * user confirms each one into "Contains" or "May contain" with one tap —
 * nothing is auto-declared without human sign-off.
 */
export function AllergenDetectWarning({ ingredients, declared = [], mayContain = [], onAddContains, onAddMayContain, onAddAllContains }) {
  const detected = detectAllergens(ingredients);
  const undeclared = detected.filter((id) => !declared.includes(id) && !(mayContain || []).includes(id));
  if (!undeclared.length) return null;
  return (
    <div data-testid="allergen-detect-warning" className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-amber-900 font-semibold text-sm">
          <AlertTriangle className="w-4 h-4" />
          Possible undeclared allergen{undeclared.length > 1 ? "s" : ""} detected
        </div>
        {undeclared.length > 1 && (
          <Button
            type="button"
            size="sm"
            data-testid="allergen-add-all-contains"
            onClick={() => onAddAllContains?.(undeclared)}
            className="h-7 text-xs bg-amber-700 hover:bg-amber-600 text-white shrink-0"
          >
            Add all to Contains
          </Button>
        )}
      </div>
      <p className="text-xs text-amber-800">
        Found in the ingredient text but not yet declared. Add each to the correct list to stay PEAL-compliant.
      </p>
      <div className="space-y-1.5">
        {undeclared.map((id) => (
          <div
            key={id}
            data-testid={`allergen-warn-${id}`}
            className="flex items-center justify-between gap-2 bg-white/70 rounded px-2 py-1.5"
          >
            <span className="text-sm font-medium text-amber-900">{declarationName(id)}</span>
            <div className="flex gap-1.5">
              <Button
                type="button"
                size="sm"
                data-testid={`allergen-add-contains-${id}`}
                onClick={() => onAddContains?.(id)}
                className="h-7 text-xs bg-amber-600 hover:bg-amber-500 text-white"
              >
                Add to Contains
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                data-testid={`allergen-add-maycontain-${id}`}
                onClick={() => onAddMayContain?.(id)}
                className="h-7 text-xs border-amber-400 text-amber-900 hover:bg-amber-100"
              >
                Add to May contain
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
